import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { useModulePackage } from '@platform/modularity/useModulePackage';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { useGetCatalogQuery, useGetModuleQuery, useUpdateWorkspaceModuleMutation } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import { ColorsCard } from './ColorsCard';
import { DefinitionCard } from './DefinitionCard';
import { FieldsCard } from './FieldsCard';
import type { IRecordTypeBuilderProps } from './IRecordTypeBuilderProps';
import { JsonCard } from './JsonCard';
import { saveErrorText } from './saveErrorText';
import { TabsCard } from './TabsCard';
import { useCustomModuleEditor } from './useCustomModuleEditor';
import { useRecordTypeEditor } from './useRecordTypeEditor';
import { ViewsCard } from './ViewsCard';
import './builder.css';

/**
 * Конструктор типу запису простору (прототип v4 page==='builder'; ADR-023): «Визначення», «Поля», «Кольори значень», «Подання записів» і
 * «Вкладки форми» поруч, «Визначення JSON». Кожна зміна одразу зберігається перевизначенням простору (PUT …/override з If-Match);
 * назва головного типу — назва модуля в просторі (PATCH модуля); визначення власного модуля простору (ADR-025) — PATCH …/custom-modules.
 * Спільний для «Налаштування › Конструктор модулів» і налаштувань модуля.
 */
export function RecordTypeBuilder({ ws, module, rtKey, layoutTo }: IRecordTypeBuilderProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const dispatch = useAppDispatch();
  const key = module.module.key;
  useModulePackage(key);
  const strings = useModuleStrings(key);
  // опублікованого визначення власний модуль простору не має (ADR-025): порівнювати зміни ні з чим
  const { data: published } = useGetModuleQuery({ key }, { skip: module.module.kind === 'custom' });
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const [updateModule] = useUpdateWorkspaceModuleMutation();
  const editor = useRecordTypeEditor(ws, key, rtKey);
  const custom = useCustomModuleEditor(ws, module);
  const rt = module.recordTypes.find((r) => r.key === rtKey);
  if (!rt) return null;
  const moduleName = label(catalog?.modules.find((m) => m.key === key)?.name) || strings.moduleName(module.module.name);
  const rename = (name: string) => {
    updateModule({ ws, moduleKey: key, body: { name: name.trim() } }).unwrap()
      .catch((e: unknown) => dispatch(toastShown({ kind: 'error', text: saveErrorText(e, t('rtBuilder.conflict'), t('rtBuilder.saveFailed')) })));
  };
  const card = { module, rt, published: published?.recordTypes.find((r) => r.key === rtKey), editor, strings };
  return (
    <div className="rb">
      {editor.loaded && !editor.canEdit && <p className="rb-note">{t('rtBuilder.noRights')}</p>}
      <DefinitionCard {...card} moduleName={moduleName} categories={catalog?.categories ?? []} onRenameModule={rename} custom={custom} />
      <FieldsCard {...card} layoutTo={layoutTo} />
      <ColorsCard {...card} />
      <div className="rb-grid2">
        <ViewsCard {...card} />
        <TabsCard {...card} />
      </div>
      <JsonCard {...card} />
    </div>
  );
}
